import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, Users, Target, TrendingUp, Award, 
  Crown, Star, Zap, BarChart3, Activity 
} from 'lucide-react';

interface SalesmanData {
  name: string;
  revenue: number;
  clients: number;
  deals: number;
  performance: number;
  rank: number;
  avatar?: string;
}

interface SalesmenPerformanceWheelProps {
  title: string;
  salesmen: SalesmanData[];
}

type MetricType = 'revenue' | 'clients' | 'deals' | 'performance';

const SalesmenPerformanceWheel: React.FC<SalesmenPerformanceWheelProps> = ({ 
  title, 
  salesmen 
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('revenue');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedSalesman, setSelectedSalesman] = useState<number | null>(null);

  // Calculate totals for each metric
  const totals = {
    revenue: salesmen.reduce((sum, s) => sum + s.revenue, 0),
    clients: salesmen.reduce((sum, s) => sum + s.clients, 0),
    deals: salesmen.reduce((sum, s) => sum + s.deals, 0),
    performance: salesmen.reduce((sum, s) => sum + s.performance, 0),
  };

  // Get value based on selected metric
  const getValue = (salesman: SalesmanData) => {
    switch (selectedMetric) {
      case 'revenue': return salesman.revenue;
      case 'clients': return salesman.clients;
      case 'deals': return salesman.deals;
      case 'performance': return salesman.performance;
    }
  };

  const getTotal = () => totals[selectedMetric];

  // Calculate percentages and angles
  const salesmenWithAngles = salesmen.map((salesman, index) => {
    const value = getValue(salesman);
    const percentage = (value / getTotal()) * 100;
    return { ...salesman, value, percentage, index };
  });

  // Sort by value for better visualization
  const sortedSalesmen = [...salesmenWithAngles].sort((a, b) => b.value - a.value);

  // Calculate cumulative angles for the wheel
  let cumulativeAngle = 0;
  const salesmenWithPositions = sortedSalesmen.map((salesman) => {
    const startAngle = cumulativeAngle;
    const angleSize = (salesman.percentage / 100) * 360;
    cumulativeAngle += angleSize;
    return { ...salesman, startAngle, angleSize };
  });

  // Color palette - vibrant gradients
  const getColor = (index: number) => {
    const colors = [
      { from: '#FF6B6B', to: '#EE5A6F', glow: 'rgba(255, 107, 107, 0.4)' }, // Red
      { from: '#4ECDC4', to: '#44A08D', glow: 'rgba(78, 205, 196, 0.4)' }, // Teal
      { from: '#FFD93D', to: '#F6C90E', glow: 'rgba(255, 217, 61, 0.4)' }, // Yellow
      { from: '#A8E6CF', to: '#56C596', glow: 'rgba(168, 230, 207, 0.4)' }, // Green
      { from: '#FF8B94', to: '#FF6F91', glow: 'rgba(255, 139, 148, 0.4)' }, // Pink
      { from: '#95E1D3', to: '#38B2AC', glow: 'rgba(149, 225, 211, 0.4)' }, // Cyan
      { from: '#C7CEEA', to: '#9FA8DA', glow: 'rgba(199, 206, 234, 0.4)' }, // Purple
      { from: '#FFDAC1', to: '#FFB88C', glow: 'rgba(255, 218, 193, 0.4)' }, // Peach
    ];
    return colors[index % colors.length];
  };

  // Metric configurations
  const metrics = [
    { 
      key: 'revenue' as MetricType, 
      label: 'Revenue', 
      icon: DollarSign, 
      color: 'from-green-500 to-emerald-600',
      format: (val: number) => `$${(val / 1000).toFixed(0)}K`
    },
    { 
      key: 'clients' as MetricType, 
      label: 'Clients', 
      icon: Users, 
      color: 'from-blue-500 to-blue-600',
      format: (val: number) => val.toString()
    },
    { 
      key: 'deals' as MetricType, 
      label: 'Deals', 
      icon: Target, 
      color: 'from-purple-500 to-purple-600',
      format: (val: number) => val.toString()
    },
    { 
      key: 'performance' as MetricType, 
      label: 'Performance', 
      icon: TrendingUp, 
      color: 'from-orange-500 to-orange-600',
      format: (val: number) => `${val}%`
    },
  ];

  const currentMetric = metrics.find(m => m.key === selectedMetric)!;

  // Create SVG path for a segment
  const createArcPath = (startAngle: number, angleSize: number, radius: number, innerRadius: number) => {
    const start = (startAngle - 90) * (Math.PI / 180);
    const end = (startAngle + angleSize - 90) * (Math.PI / 180);
    
    const x1 = 200 + radius * Math.cos(start);
    const y1 = 200 + radius * Math.sin(start);
    const x2 = 200 + radius * Math.cos(end);
    const y2 = 200 + radius * Math.sin(end);
    
    const x3 = 200 + innerRadius * Math.cos(end);
    const y3 = 200 + innerRadius * Math.sin(end);
    const x4 = 200 + innerRadius * Math.cos(start);
    const y4 = 200 + innerRadius * Math.sin(start);
    
    const largeArc = angleSize > 180 ? 1 : 0;
    
    return `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;
  };

  // Get rank icon
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Award className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Star className="w-5 h-5 text-amber-600" />;
    return <Zap className="w-5 h-5 text-blue-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header with Metric Selector */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">{title}</h3>
        
        {/* Metric Toggle Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            const isSelected = selectedMetric === metric.key;
            
            return (
              <motion.button
                key={metric.key}
                onClick={() => setSelectedMetric(metric.key)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-300
                  ${isSelected
                    ? 'border-transparent shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                  }
                `}
              >
                {isSelected && (
                  <motion.div
                    layoutId="metric-bg"
                    className={`absolute inset-0 bg-gradient-to-br ${metric.color} rounded-xl`}
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <div className="relative z-10 flex flex-col items-center gap-2">
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
                  <div className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                    {metric.label}
                  </div>
                  <div className={`text-xs ${isSelected ? 'text-white/90' : 'text-gray-500'}`}>
                    {metric.format(getTotal())}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Main Wheel Visualization */}
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* SVG Wheel */}
          <div className="relative">
            <svg viewBox="0 0 400 400" className="w-full max-w-md mx-auto">
              {/* Background circle */}
              <circle cx="200" cy="200" r="180" fill="#F3F4F6" />

              {/* Segments */}
              {salesmenWithPositions.map((salesman, idx) => {
                const color = getColor(idx);
                const isHovered = hoveredIndex === idx;
                const isSelected = selectedSalesman === idx;
                const radius = isHovered || isSelected ? 185 : 180;
                const innerRadius = isHovered || isSelected ? 65 : 70;

                return (
                  <g key={salesman.name}>
                    {/* Segment path */}
                    <motion.path
                      d={createArcPath(salesman.startAngle, salesman.angleSize, radius, innerRadius)}
                      fill={`url(#gradient-${idx})`}
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-300"
                      onMouseEnter={() => setHoveredIndex(idx)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setSelectedSalesman(isSelected ? null : idx)}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05, duration: 0.5 }}
                      style={{
                        filter: isHovered || isSelected
                          ? `drop-shadow(0 0 12px ${color.glow})`
                          : 'none'
                      }}
                    />

                    {/* Gradient definition */}
                    <defs>
                      <linearGradient id={`gradient-${idx}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={color.from} />
                        <stop offset="100%" stopColor={color.to} />
                      </linearGradient>
                    </defs>

                    {/* Percentage label */}
                    {salesman.angleSize > 20 && (
                      <text
                        x={200 + 125 * Math.cos((salesman.startAngle + salesman.angleSize / 2 - 90) * Math.PI / 180)}
                        y={200 + 125 * Math.sin((salesman.startAngle + salesman.angleSize / 2 - 90) * Math.PI / 180)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-sm font-bold fill-white pointer-events-none"
                        style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
                      >
                        {salesman.percentage.toFixed(1)}%
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Center circle with total */}
              <circle cx="200" cy="200" r="65" fill="white" filter="drop-shadow(0 4px 12px rgba(0,0,0,0.1))" />

              {/* Center content */}
              <text x="200" y="185" textAnchor="middle" className="text-xs fill-gray-500 font-medium">
                Total {currentMetric.label}
              </text>
              <text x="200" y="210" textAnchor="middle" className="text-2xl fill-gray-900 font-bold">
                {currentMetric.format(getTotal())}
              </text>
            </svg>

            {/* Hover tooltip */}
            <AnimatePresence>
              {hoveredIndex !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl z-10"
                >
                  <div className="text-sm font-semibold">
                    {salesmenWithPositions[hoveredIndex].name}
                  </div>
                  <div className="text-xs text-gray-300">
                    {currentMetric.format(salesmenWithPositions[hoveredIndex].value)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Legend & Details */}
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {salesmenWithPositions.map((salesman, idx) => {
              const color = getColor(idx);
              const isSelected = selectedSalesman === idx;

              return (
                <motion.div
                  key={salesman.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedSalesman(isSelected ? null : idx)}
                  className={`
                    p-4 rounded-xl border-2 cursor-pointer transition-all duration-300
                    ${isSelected
                      ? 'border-gray-400 shadow-lg bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {/* Color indicator */}
                      <div
                        className="w-4 h-4 rounded-full shadow-md"
                        style={{
                          background: `linear-gradient(135deg, ${color.from}, ${color.to})`,
                          boxShadow: `0 0 8px ${color.glow}`
                        }}
                      />

                      {/* Name and rank */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{salesman.name}</span>
                          {getRankIcon(salesman.rank)}
                        </div>
                        <div className="text-xs text-gray-500">Rank #{salesman.rank}</div>
                      </div>
                    </div>

                    {/* Value */}
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {currentMetric.format(salesman.value)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {salesman.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        background: `linear-gradient(90deg, ${color.from}, ${color.to})`,
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${salesman.percentage}%` }}
                      transition={{ duration: 1, delay: idx * 0.05 }}
                    />
                  </div>

                  {/* Expanded details */}
                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-3 pt-3 border-t border-gray-200 overflow-hidden"
                      >
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <div>
                              <div className="text-gray-500">Revenue</div>
                              <div className="font-semibold text-gray-900">
                                ${(salesman.revenue / 1000).toFixed(0)}K
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <div>
                              <div className="text-gray-500">Clients</div>
                              <div className="font-semibold text-gray-900">{salesman.clients}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-purple-600" />
                            <div>
                              <div className="text-gray-500">Deals</div>
                              <div className="font-semibold text-gray-900">{salesman.deals}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-orange-600" />
                            <div>
                              <div className="text-gray-500">Performance</div>
                              <div className="font-semibold text-gray-900">{salesman.performance}%</div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesmenPerformanceWheel;

